import {
	type ChangeEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { INCREMENTAL_COUNTER_MAX } from "web_myplann/Shared/constants";
import { useHoldRepeat } from "web_myplann/Shared/Hooks/useHoldRepeat";
import { parseCounterInput } from "web_myplann/Shared/Utils/parseCounterInput";
import { stepDown } from "web_myplann/Shared/Utils/stepDown";
import { stepUp } from "web_myplann/Shared/Utils/stepUp";

type Params = {
	value: number;
	onChange: (value: number) => void;
	step: number;
	hold: {
		startDelayMs: number;
		intervalMs: number;
	};
};

export const useSteppedValue = ({ value, onChange, step, hold }: Params) => {
	const [editing, setEditing] = useState(false);
	const valueRef = useRef(value);
	const { pressHandlers } = useHoldRepeat(hold);

	useEffect(() => {
		valueRef.current = value;
	}, [value]);

	const stepBy = useCallback(
		(direction: 1 | -1) => {
			const next =
				direction > 0
					? stepUp(valueRef.current, step, INCREMENTAL_COUNTER_MAX)
					: stepDown(valueRef.current, step);
			valueRef.current = next;
			onChange(next);
		},
		[onChange, step],
	);

	const handleFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
		onChange(parseCounterInput(event.target.value));
	};

	return {
		editing,
		startEditing: () => setEditing(true),
		stopEditing: () => setEditing(false),
		handleFieldChange,
		decrementHandlers: pressHandlers(() => stepBy(-1)),
		incrementHandlers: pressHandlers(() => stepBy(1)),
		canDecrement: value > 0,
		canIncrement: value < INCREMENTAL_COUNTER_MAX,
	};
};
